'use client';

import type { Route } from 'next';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { Locale } from '@/i18n/config';
import { locales } from '@/i18n/config';

type LanguageSwitcherProps = {
  currentLocale: Locale;
};

const LANGUAGE_CODES: Record<Locale, string> = {
  en: 'EN',
  pt: 'PT',
  es: 'ES',
};

export function LanguageSwitcher({ currentLocale }: LanguageSwitcherProps) {
  const t = useTranslations('common');
  const router = useRouter();
  const pathname = usePathname();

  const switchLocale = (newLocale: Locale) => {
    if (newLocale === currentLocale) {
      return;
    }

    const segments = pathname.split('/').filter(Boolean);
    const hasLocalePrefix = locales.includes(segments[0] as Locale);

    let newPath = '';
    if (hasLocalePrefix) {
      segments[0] = newLocale;
      newPath = `/${segments.join('/')}`;
    } else {
      newPath = `/${newLocale}${pathname.startsWith('/') ? pathname : `/${pathname}`}`;
    }

    router.push(newPath as Route);
  };

  return (
    <label>
      <span className="sr-only">{t('language')}</span>
      <select
        aria-label={t('language')}
        onChange={(event) => switchLocale(event.target.value as Locale)}
        value={currentLocale}
      >
        {locales.map((locale) => (
          <option key={locale} value={locale}>
            {LANGUAGE_CODES[locale]}
          </option>
        ))}
      </select>
    </label>
  );
}
